import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import { useIsMobile } from "../hooks/useIsMobile";
import { useT } from "../i18n/LanguageProvider";
import { resolveShare } from "../api/share";
import { fetchAcceptedProfile } from "../api/candidates";
import { BrandLogo } from "../components/BrandLogo";
import { SafeLogo } from "../components/SafeLogo";
import { StatusBadge } from "../components/StatusBadge";
import { isDisplayableUrl } from "../utils/imageUrl";
import { C, font } from "../styles/tokens";

/**
 * SharedProfilePage - the verifier-facing view at /share/:token, and at
 * /candidates/requests/:requestId/profile for a candidate who accepted the
 * organisation's profile request (no share link involved).
 *
 * Access control:
 *   - Not signed in: redirect to /login with the share URL as the post-login target
 *   - Signed in as a Professional: shown a forbidden message (verifier-only)
 *   - Signed in as an Institution / Organisation officer or Admin: profile loads
 *
 * What's shown:
 *   - Identity (name, location, bio)
 *   - Credentials grouped by type (Education / Work / Other)
 *   - Each credential's status badge, dates, issuer, description
 *   - A "Read-only view" banner at the top so it's clear this is a shared profile
 *   - Footer note: "This profile is shared verifier-only. Every view is logged."
 *
 * Not shown (intentionally):
 *   - Contact info (email, phone)
 *   - Certificate file contents (a future enhancement will allow short-lived
 *     signed URLs for verifiers who need to inspect)
 */
export function SharedProfilePage() {
  const { token, profileId, requestId } = useParams();
  const resolvedToken = profileId || token;
  const t = useT();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { localUser, loading: authLoading } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const isVerifier =
    localUser?.type === "INSTITUTION_OFFICER" ||
    localUser?.type === "ORGANISATION_OFFICER" ||
    localUser?.type === "PLATFORM_ADMIN";

  const isOwner =
    localUser &&
    profile &&
    (localUser.id === profile.userId ||
      (localUser.professional && localUser.professional.id === profile.id));

  useEffect(() => {
    // Wait for auth state to settle
    if (authLoading) return;

    // Verification requests require authentication and verifier role
    if (requestId) {
      if (!localUser) {
        navigate(
          `/login?next=${encodeURIComponent(
            `/candidates/requests/${requestId}/profile`,
          )}`,
          { replace: true },
        );
        return;
      }

      if (!isVerifier) {
        setForbidden(true);
        setLoading(false);
        return;
      }
    }

    // Load profile (publicly accessible for share links, verifier-checked for request links)
    (async () => {
      try {
        const data = requestId
          ? await fetchAcceptedProfile(requestId)
          : await resolveShare(resolvedToken);
        setProfile(data);
      } catch (err) {
        if (requestId) {
          // The server says why: not accepted, withdrawn, or not found.
          setError(err.message ?? t("common.somethingWrong"));
        } else if (err.status === 404 || err?.response?.status === 404) {
          setError("This share link is invalid or has been disabled.");
        } else if (err.status === 403 || err?.response?.status === 403) {
          setForbidden(true);
        } else {
          setError(err.message ?? t("common.somethingWrong"));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [
    token,
    profileId,
    resolvedToken,
    requestId,
    localUser,
    isVerifier,
    authLoading,
  ]);

  if (authLoading || loading) {
    return (
      <div style={pageLoadingStyle}>
        <div style={{ color: C.textMed }}>{t("common.loading")}</div>
      </div>
    );
  }

  if (forbidden) {
    return <ForbiddenScreen onSignOut={() => navigate("/")} />;
  }

  if (error) {
    return (
      <ErrorScreen
        message={error}
        onBack={() => navigate(requestId ? "/candidates" : "/")}
      />
    );
  }

  return (
    <div
      style={{
        background: C.surface,
        minHeight: "100vh",
        fontFamily: font,
        color: C.textDark,
      }}
    >
      {/* Header bar */}
      <div
        style={{
          background: C.bg,
          borderBottom: `1px solid ${C.divider}`,
          padding: "12px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate("/");
              }
            }}
            style={{
              background: "transparent",
              border: 0,
              color: C.blue,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: font,
            }}
          >
            Back
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {isOwner && (
            <Link
              to="/profile"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                color: C.blue,
                background: C.blueLight,
                padding: "5px 12px",
                borderRadius: 4,
                textDecoration: "none",
                fontFamily: font,
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit Profile
            </Link>
          )}

          {!localUser && (
            <Link
              to={`/login?next=${encodeURIComponent(
                requestId
                  ? `/candidates/requests/${requestId}/profile`
                  : `/profile/${resolvedToken}`,
              )}`}
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: C.blue,
                textDecoration: "none",
                fontFamily: font,
                padding: "4px 8px",
              }}
            >
              Sign In
            </Link>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: C.blueDark,
              background: C.blueLight,
              padding: "4px 12px",
              borderRadius: 12,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Read-only - View logged
          </div>
        </div>
      </div>

      {/* Profile card -- matches prototype S-01 exactly */}
      <div
        style={{
          maxWidth: 720,
          margin: "24px auto",
          background: C.bg,
          border: `1px solid ${C.divider}`,
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {/* Cover band gradient -- 80px mobile / 120px desktop */}
        <div
          style={{
            height: isMobile ? 80 : 120,
            background: `linear-gradient(135deg, ${C.blue} 0%, ${C.blueDark} 100%)`,
          }}
        />

        {/* Identity -- avatar overlaps cover band with negative margin */}
        <div
          style={{
            padding: isMobile ? "0 20px 20px" : "0 32px 32px",
            marginTop: isMobile ? -32 : -40,
          }}
        >
          <AvatarLarge
            firstName={profile.firstName}
            lastName={profile.lastName}
            photoUrl={profile.avatarUrl}
            size={isMobile ? 80 : 112}
          />
          <div style={{ marginTop: 12 }}>
            <h1
              style={{
                fontSize: isMobile ? 22 : 28,
                fontWeight: 700,
                color: C.textDark,
                margin: "0 0 4px",
                letterSpacing: -0.3,
              }}
            >
              {profile.firstName} {profile.lastName}
            </h1>
            <div style={{ fontSize: 15, color: C.textMed, marginBottom: 10 }}>
              {profile.isStudent
                ? t("profile.student")
                : t("profile.professional")}
              {profile.city && (
                <>
                  {" "}
                  - {profile.city}
                  {profile.state && `, ${profile.state}`}
                </>
              )}
            </div>
            {profile.stats.verified > 0 && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  background: C.greenLight,
                  color: C.green,
                  borderRadius: 12,
                  fontWeight: 600,
                  padding: "3px 12px",
                  fontSize: 12,
                }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {profile.stats.verified} credential
                {profile.stats.verified !== 1 ? "s" : ""} verified
              </span>
            )}
          </div>
        </div>

        {/* Skills section */}
        {profile.skills && profile.skills.length > 0 && (
          <div style={{ padding: isMobile ? "0 20px 16px" : "0 32px 20px" }}>
            <div
              style={{ height: 1, background: C.divider, marginBottom: 16 }}
            />
            <h2
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: C.textDark,
                margin: "0 0 12px",
                letterSpacing: "-0.2px",
              }}
            >
              Skills
            </h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {profile.skills.map((skill, index) => (
                <span
                  key={index}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#1e40af",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 16,
                    padding: "4px 12px",
                    lineHeight: 1.3,
                  }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Credential sections */}
        <div style={{ padding: isMobile ? "0 20px" : "0 32px" }}>
          <CredentialsByType
            type="EDUCATION"
            title={t("profile.education")}
            credentials={profile.credentials.filter(
              (c) => c.type === "EDUCATION",
            )}
          />
          <CredentialsByType
            type="WORK"
            title={t("profile.workExperience")}
            credentials={profile.credentials.filter((c) => c.type === "WORK")}
          />
          <CredentialsByType
            type="OTHER"
            title={t("profile.others")}
            credentials={profile.credentials.filter((c) => c.type === "OTHER")}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: `1px solid ${C.divider}`,
            padding: 20,
            textAlign: "center",
            fontSize: 11,
            color: C.textLight,
            lineHeight: 1.6,
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <BrandLogo to="/" mark={16} text={11} />
          </div>
          All entries independently verified by issuing institutions.
        </div>
      </div>
    </div>
  );
}

// ---- subcomponents ----

function AvatarLarge({ firstName, lastName, photoUrl, size }) {
  const [loadFailed, setLoadFailed] = useState(false);
  const initials =
    `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
  const showPhoto = !loadFailed && isDisplayableUrl(photoUrl);

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: showPhoto ? C.blueLight : C.bg,
        border: `3px solid ${C.bg}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.36,
        fontWeight: 700,
        color: C.blue,
        fontFamily: font,
        overflow: "hidden",
      }}
    >
      {showPhoto ? (
        <img
          src={photoUrl}
          alt={`${firstName ?? ""} ${lastName ?? ""}`.trim()}
          onError={() => setLoadFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        initials
      )}
    </div>
  );
}

function CredentialsByType({ title, credentials }) {
  if (credentials.length === 0) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ height: 1, background: C.divider }} />
      <h2
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: C.textDark,
          margin: "16px 0 8px",
        }}
      >
        {title}
      </h2>
      {credentials.map((c, i) => (
        <CredentialRow key={c.id} c={c} showDivider={i > 0} />
      ))}
    </div>
  );
}

function CredentialRow({ c, showDivider }) {
  return (
    <div
      style={{
        padding: "12px 0",
        borderTop: showDivider ? `1px solid ${C.divider}` : "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 2,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600, color: C.textDark }}>
              {c.title}
            </span>
            <StatusBadge status={c.status} />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              margin: "4px 0",
            }}
          >
            <SafeLogo
              src={c.issuerLogoUrl}
              alt={c.issuer || "Issuer"}
              fallbackText={
                c.issuer ? c.issuer.slice(0, 2).toUpperCase() : "TP"
              }
              imgStyle={{
                width: 26,
                height: 26,
                borderRadius: 6,
                objectFit: "contain",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                padding: 2,
                flexShrink: 0,
              }}
              fallbackStyle={{
                width: 26,
                height: 26,
                borderRadius: 6,
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
              }}
            />
            <div
              style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: C.textMed }}>
                {c.issuer}
              </div>
              {c.issuerDescription && (
                <div style={{ fontSize: 11, color: C.textLight, marginTop: 1 }}>
                  {c.issuerDescription}
                </div>
              )}
            </div>
          </div>
          {c.startDate && (
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
              {formatDateRange(c.startDate, c.endDate, c.isCurrent)}
            </div>
          )}
          {c.description && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 10px",
                background: C.bgMuted,
                borderRadius: 4,
                fontSize: 12,
                color: C.textDark,
                lineHeight: 1.5,
                whiteSpace: "pre-line",
              }}
            >
              {c.description}
            </div>
          )}
          {c.hasCertificate && (
            <div
              style={{
                fontSize: 11,
                color: C.textLight,
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              {c.certificateFileName ?? "Certificate attached"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- forbidden / error screens ----

function ForbiddenScreen() {
  return (
    <div style={fullPageStyle}>
      <div style={{ maxWidth: 480, width: "100%" }}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: C.amberLight,
              color: C.amber,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
            Verifier accounts only
          </h1>
          <p
            style={{
              fontSize: 14,
              color: C.textMed,
              lineHeight: 1.5,
              margin: "0 0 16px",
            }}
          >
            This verification request is accessible only to authorized verifiers
            (institutions, organisations, and platform admins).
          </p>
          <p style={{ fontSize: 12, color: C.textLight, marginBottom: 16 }}>
            If you represent an institution or organisation, sign up to view
            shared profiles.
          </p>
          <Link
            to="/"
            style={{
              display: "inline-block",
              padding: "10px 22px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              background: C.blue,
              border: 0,
              borderRadius: 4,
              textDecoration: "none",
              fontFamily: font,
            }}
          >
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ message, onBack }) {
  return (
    <div style={fullPageStyle}>
      <div style={{ maxWidth: 480, width: "100%" }}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: C.redLight,
              color: C.red,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
            Profile not available
          </h1>
          <p
            style={{
              fontSize: 14,
              color: C.textMed,
              lineHeight: 1.5,
              margin: "0 0 16px",
            }}
          >
            {message}
          </p>
          <button
            onClick={onBack}
            style={{
              padding: "10px 22px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              background: C.blue,
              border: 0,
              borderRadius: 4,
              cursor: "pointer",
              fontFamily: font,
            }}
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- helpers ----

function formatDateRange(start, end, isCurrent) {
  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          month: "short",
          year: "numeric",
        })
      : "";
  const s = fmt(start);
  const e = isCurrent ? "Present" : fmt(end);
  if (s && e) return `${s} - ${e}`;
  return s || e;
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 24,
};

const pageLoadingStyle = {
  minHeight: "100vh",
  background: C.bgMuted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: font,
  color: C.textDark,
  padding: 16,
};

const fullPageStyle = {
  minHeight: "100vh",
  background: C.bgMuted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: font,
  color: C.textDark,
  padding: 16,
};
